import { Box, CssBaseline, Container, IconButton, LinearProgress, Stack } from "@mui/material";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { KeyboardArrowLeft, Menu as MenuIcon } from "@mui/icons-material";

import { Root, Header, EdgeTrigger, EdgeSidebar, SidebarContent, Content, Footer } from "@mui-treasury/layout";
import { ConfirmProvider } from "material-ui-confirm";
import { ToastContainer } from "react-toastify";
import { ErrorBoundary } from "react-error-boundary";

import Routes from "./components/RoutesTree";
import Menu from "./components/Menu";
import FooterContent from "./common/components/Footer";
import HeaderContent from "./components/shared/Header";
// import Breadcrumbs from "./components/shared/Breadcrumbs";
import { useAxiosLoader } from "./common/hooks/useAxiosLoader";
import GeneralError from "./common/components/pages/GeneralError";
import logService from "./common/services/logService";

import theme from "./theme";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";

export default function App() {
  const loading = useAxiosLoader();

  const errorHandler = async (error: Error, info: { componentStack: string }) => {
    await logService.error(JSON.stringify({ message: error.message, stack: error.stack })).finally(() => {
      console.log(error);
      console.log(info.componentStack);
    });
  };

  const renderHeader = () => (
    <Header color="primary">
      <Stack direction="row" sx={{ mt: "auto", mb: "auto", ml: 2, mr: 2 }}>
        <EdgeTrigger target={{ anchor: "left", field: "open" }}>
          {(open: boolean, setOpen: (isOpen: boolean) => void) => (
            <IconButton onClick={() => setOpen(!open)} edge="end" sx={{ pr: 3 }}>
              {open ? <KeyboardArrowLeft sx={{ fill: "white" }} /> : <MenuIcon sx={{ fill: "white" }} />}
            </IconButton>
          )}
        </EdgeTrigger>
        <HeaderContent />
      </Stack>
      {loading && (
        <Box width="100%">
          <LinearProgress color="secondary" sx={{ height: 8 }} />
        </Box>
      )}
    </Header>
  );

  const renderSidebar = () => (
    <EdgeSidebar anchor="left">
      <>
        <SidebarContent sx={{ mt: -1 }}>
          <Menu />
        </SidebarContent>
      </>
    </EdgeSidebar>
  );

  const renderFooter = () => (
    <Footer>
      <FooterContent />
    </Footer>
  );

  return (
    <ThemeProvider theme={theme}>
      <ToastContainer style={{ width: "470px", overflowWrap: "anywhere" }} />
      <StyledEngineProvider injectFirst>
        <ErrorBoundary FallbackComponent={GeneralError} onError={errorHandler}>
          <Root
            scheme={{
              header: {
                config: {
                  xs: {
                    position: "sticky",
                    height: 64,
                    clipped: true,
                  },
                },
              },
              leftEdgeSidebar: {
                config: {
                  md: {
                    variant: "persistent",
                    persistentBehavior: "fit",
                    width: 320,
                    collapsible: true,
                    collapsedWidth: 320,
                    headerMagnetEnabled: true,
                  },
                },
              },
            }}
            initialState={{
              leftEdgeSidebar: {
                open: true,
                collapsed: true,
              },
            }}
          >
            <CssBaseline />
            {renderHeader()}
            {renderSidebar()}
            <Content sx={{ pl: 1, pr: 1 }}>
              <Container maxWidth="lg" sx={{ pt: 2 }}>
                <ConfirmProvider
                  defaultOptions={{
                    confirmationText: "Да",
                    cancellationText: "Не",
                  }}
                >
                  {/* <Breadcrumbs /> */}
                  <Routes />
                </ConfirmProvider>
              </Container>
            </Content>
            {renderFooter()}
          </Root>
        </ErrorBoundary>
      </StyledEngineProvider>
    </ThemeProvider>
  );
}
